<template>
  <page-wrapper ref="pageWrapperRef" :transparent-bg="!!$vuetify.display.smAndDown">
    <reconciliation-details-dialog
      v-model="isDetailsDialogOpen"
      ref="detailsDialogRef"
      :reconciliation-data="selectedReconciliation"
      :period-status="reconciliationStatus?.contractReconciliationState"
      :can-edit="isOperationVisible(reconciliationOperations?.edit)"
      :mismatch-from-menu="isMismatchFromMenu"
      @after-leave="handleDialogAfterLeave"
      @reconcile="handleReconcileFromDialog"
      @status-changed="handleStatusChanged"
      @mismatch="handleMismatch"
    >
      <template #reconcile-button="{ loading, disabled, onClick, canEdit }">
        <e-btn 
          variant="primary" 
          size="L" 
          class="elevation-0" 
          :loading="loading" 
          :disabled="disabled || !canEdit"  
          @click="onClick"
        >
          Сверить
        </e-btn>
      </template>
    </reconciliation-details-dialog>

    <template #title>
      <div class="h3">Сверка</div>
    </template>
    <template
      v-if="reconciliationStatus?.contractReconciliationState === ContractReconciliationStates.HasNotReconciledOrders"
      #subtitle
    >
      <e-alert class="w-100">
        <div class="paragraph-m_h font-600">В разделе присутствуют несверенные брони</div>
        <div class="paragraph-s text-secondary mt-1">
          11-го числа месяца они будут сверены автоматически и отправлены в бухгалтерию
        </div>
      </e-alert>
    </template>
    <template
      v-else-if="
        reconciliationStatus?.contractReconciliationState === ContractReconciliationStates.AvailableToReconcile
      "
      #subtitle
    >
      <e-alert class="w-100" variant="info">
        <div class="d-flex w-100">
          <div>
            <div class="paragraph-m_h font-600">Бронирования за отчетный месяц сверены</div>
            <div class="paragraph-s text-secondary mt-1">
              Вы можете завершить сверку сейчас или 11-го числа месяца она будет завершена автоматически
            </div>
          </div>

          <v-spacer />
          <e-btn 
            v-if="isOperationVisible(reconciliationOperations?.edit)" 
            variant="primary" 
            @click="openCompletePeriodDialog"
            :loading="isCompletingPeriodReconciliation" 
            :disabled="isOperationDisabled(reconciliationOperations?.edit) || isCompletingPeriodReconciliation"
          >
            Завершить сверку
          </e-btn>
        </div>
      </e-alert>
    </template>

    <reconciliation-filters-component v-model="filters" />

    <div
      v-if="reconciliationStatus?.contractReconciliationState === ContractReconciliationStates.Reconciled"
      class="paragraph-m_h my-4 my-md-6"
    >
      Бронирования за выбранный отчетный месяц сверены. Ознакомиться с отчетными документами можно в разделе
      <router-link :to="{ name: RouteNames.Documents }" class="link">Документы.</router-link>
    </div>

    <div
      v-if="isReconciliationsLoading || reconciliations?.ordersReconciliation.length"
      class="paragraph-s_h my-4 my-md-6 d-flex flex-wrap col-gap-6 row-gap-1"
    >
      <div class="font-600 w-100 w-sm-auto">Итого:</div>

      <div>
        <span class="text-secondary mr-1">Выплаты</span>
        <skeleton-loader v-if="isReconciliationsLoading" inline width="100px" height="16px" />
        <b v-else-if="reconciliations">
          {{ formatPrice(reconciliations.totalAmountsInfo.payout) }}
        </b>
      </div>

      <div>
        <span class="text-secondary mr-1">К&nbsp;оплате</span>
        <skeleton-loader v-if="isReconciliationsLoading" inline width="100px" height="16px" />
        <b v-else-if="reconciliations"> {{ formatPrice(reconciliations.totalAmountsInfo.payable) }} </b>
      </div>
    </div>

    <template v-if="isReconciliationsLoading || reconciliations?.ordersReconciliation.length">
      <e-data-table
        v-if="$vuetify.display.mdAndUp"
        v-model="selected"
        v-model:sort-by="filters.sort"
        disable-sort
        :items="reconciliations?.ordersReconciliation || []"
        :headers="headers"
        :loading="isReconciliationsLoading"
        :styled-header="!!selected.length"
        :show-select="!isReconciled && isOperationVisible(reconciliationOperations?.edit)"
        :show-select-column="!isReconciled && isOperationVisible(reconciliationOperations?.edit)"
        interactive
        return-object
        content-wrap
        :class="{ 'table-loading': loadingReconciliationId !== null }"
        @click:row="handleRowClick"
      >

      <template #header.data-table-select>
        <e-checkbox
          :model-value="isAllSelected"
          :square-selected="isPartialSelected"
          :disabled="isReconciliationsLoading"
          @update:modelValue="onAllSelectedUpdate"
        />
      </template>

        <template v-if="selected.length" #headers="{ someSelected, allSelected, selectAll }">
          <tr>
            <th colspan="2">
              <e-checkbox
                label="Выбрать все"
                :model-value="allSelected"
                :square-selected="someSelected"
                data-ignore-click
                @update:model-value="selectAll(!allSelected)"
              />
            </th>
            <th colspan="3" />
            <th>
              <div>
                <div class="th text-ordinary mb-1">Выплаты</div>
                <div class="td font-600 text-black">{{ formatPrice(selectedAmountInfo.payout) }}</div>
              </div>
            </th>
            <th>
              <div>
                <div class="th text-ordinary mb-1">К оплате</div>
                <div class="td font-600 text-black">{{ formatPrice(selectedAmountInfo.payable) }}</div>
              </div>
            </th>
            <th colspan="2">
              <div>
                <e-btn variant="primary" @click="onReconcileClick">Сверить</e-btn>
              </div>
            </th>
          </tr>
        </template>

        <template v-slot:[`header.commonInfo.orderNumber`]="props">
          <div class="th">
            {{ props.column.title }}
            <help-tooltip :icon-size="16">
              Номер бронирования в экстранет и в вашем менеджере каналов для возможности быстрого поиска
            </help-tooltip>
          </div>
        </template>

        <template v-slot:[`item.commonInfo.tourists`]="{ value }">
          <div class="d-flex align-center">
            {{ value.length }}
            <help-tooltip :icon-size="16" class="ml-1">
              <p
                v-for="(item, index) in value"
                :key="index"
                class="text-uppercase"
                :class="{ 'mb-1': index !== value.length - 1 }"
              >
                {{ item.name }}
              </p>
            </help-tooltip>
          </div>
        </template>

        <template v-slot:[`item.commonInfo.orderNumber`]="{ item }">
          <router-link :to="{ name: RouteNames.Book, params: { b_id: item.commonInfo.orderId } }" class="link" target="_blank" @click.stop>
            {{ item.commonInfo.reconciliationItemTitle }}
          </router-link>
          <br />
          {{ item.commonInfo.reconciliationItemSubTitle }}
        </template>

        <template v-slot:[`item.commonInfo.period`]="{ value }">
          {{ getDatePeriod(value.from, value.to, true) }}
        </template>

        <template v-slot:[`item.commonInfo.amountsInfo.payout`]="{ value }">
          <div class="text-end text-no-wrap">
            {{ formatPrice(value) }}
          </div>
        </template>
        <template v-slot:[`item.commonInfo.amountsInfo.payable`]="{ value }">
          <div class="text-end text-no-wrap">
            {{ formatPrice(value) }}
          </div>
        </template>

        <template v-slot:[`item.commonInfo.orderReconciliationStatus`]="{ value }">
          <reconciliation-status :status="value" />
        </template>

        <template v-slot:[`item.commonInfo.orderAvoidanceStatus`]="{ value }">
          <div class="d-flex align-center">
            <e-tooltip 
              v-if="value === OrderAvoidanceStatus.Avoided || value === OrderAvoidanceStatus.PartiallyAvoided"
            >
              <template #activator>
                <cancelled-status :status="value" />
              </template>
              <div class="paragraph-xs text-secondary">
                <span v-if="value === OrderAvoidanceStatus.Avoided">
                  Бронирование аннулировано
                </span>
                <span v-else-if="value === OrderAvoidanceStatus.PartiallyAvoided">
                  Аннулирован номер / пакет / услуга
                </span>
              </div>
            </e-tooltip>
            <cancelled-status v-else :status="value" />
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <list-context-menu
            :item="item"
            :period-status="reconciliationStatus?.contractReconciliationState"
            @reconciled="handleOrderReconciled"
            @view-details="handleContextMenuViewDetails(item)"
            @mismatch-click="handleContextMenuMismatch(item)"
            :can-edit="isOperationVisible(reconciliationOperations?.edit)"
          />
        </template>

        <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, toggleSelect }">
          <e-checkbox
            v-if="!isReconciled && isOperationVisible(reconciliationOperations?.edit)" 
            :model-value="isSelected(internalItem)"
            @click.stop
            @update:modelValue="toggleSelect(internalItem, $event)"
          />
        </template>
      </e-data-table>

      <template v-else>
        <template v-if="isReconciliationsLoading">
          <skeleton-loader v-for="item in 10" height="160px" class="mb-4" :key="item" />
        </template>

        <div v-else class="d-flex flex-column row-gap-4">
          <mobile-reconciliation-item
            v-for="item in reconciliations?.ordersReconciliation"
            :model-value="selected.includes(item)"
            :key="item.commonInfo.orderId"
            :period-status="reconciliationStatus?.contractReconciliationState"
            :item="item"
            :hide-checkbox="isReconciled || !isOperationVisible(reconciliationOperations?.edit)"  
            :can-edit="isOperationVisible(reconciliationOperations?.edit)"
            @update:model-value="!isReconciled && isOperationVisible(reconciliationOperations?.edit) && onItemSelect(item)"
            @click="handleRowClick($event, { item })"
            @view-details="handleContextMenuViewDetails"
            @reconciled="handleOrderReconciled"
            @mismatch-click="handleContextMenuMismatch(item)"
          />
        </div>
      </template>
    </template>

    <pagination
      v-if="reconciliations?.ordersReconciliation.length"
      v-model="filters.pagination.page"
      class="mt-6"
      :page-size="filters.pagination.pageSize"
      :count="reconciliations?.count"
    />

    <page-content-placeholder
      v-else-if="!reconciliations?.ordersReconciliation.length"
      :img-src="PlaceholderImage"
      text="Записи для сверки не найдены"
    />

    <mobile-floating-bar v-if="$vuetify.display.smAndDown && isOperationVisible(reconciliationOperations?.edit)" :class="{ 'bg-color-light': selected.length }">
      <div class="reconciliation-mobile-floating-bar">
        <e-checkbox
          v-model="isAllSelected"
          :square-selected="isPartialSelected"
          :label="isPartialSelected ? '' : 'Выбрать все'"
          @update:model-value="onAllSelectedUpdate"
        />

        <template v-if="selected.length">
          <v-spacer />

          <div>
            <div class="reconciliation-mobile-floating-bar__item">
              <div class="th text-ordinary mr-2">Выплаты</div>
              <div class="td font-600 text-black">{{ formatPrice(selectedAmountInfo.payout) }}</div>
            </div>
            <div class="reconciliation-mobile-floating-bar__item">
              <div class="th text-ordinary mr-2">К оплате</div>
              <div class="td font-600 text-black">{{ formatPrice(selectedAmountInfo.payable) }}</div>
            </div>
          </div>

          <e-btn variant="primary" class="ml-4" @click="onReconcileClick" :disabled="selected.length === 0">
            Сверить
          </e-btn>
        </template>
      </div>
    </mobile-floating-bar>
  </page-wrapper>

  <confirmation-dialog
    v-model="isPeriodAvailableDialogOpen"
    :action="completePeriodReconciliation"
    confirm-text="Завершить сверку"
    cancel-text="Отмена"
    confirm-btn-variant="primary"
  >
    <div class="h3 mb-6">Завершение сверки</div>
    <div class="paragraph-l text-secondary">
      Бронирования за отчетный месяц сверены. Вы можете завершить сверку сейчас или 11-го числа месяца она будет завершена автоматически. 
    </div>
    <div class="paragraph-l text-secondary font-600 mt-2">
      После завершения сверки внести в нее изменения будет невозможно.
    </div>
  </confirmation-dialog>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import CancelledStatus from '../components/cancelled-status.vue';
import MobileReconciliationItem from '../components/mobile-reconciliation-item.vue';
import ReconciliationFiltersComponent from '../components/reconciliation-filters.vue';
import ReconciliationStatus from '../components/reconciliation-status.vue';
import { useReconciliationStore } from '../store';
import { getReconciliationDefaultFilters, SORT_COLUMN_MAP } from '../utils';
import type { Reconciliation, ReconciliationFilters } from '../types';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import {
  ContractReconciliationStates,
  OrderAvoidanceStatus,
  OrderReconciliationStatus,
  useGetContractReconciliationAvailableForPeriodQuery,
  useGetOrderReconciliationQuery,
  useReconcileContractForPeriodMutation,
  useReconcileOrdersReconciliationMutation,
} from '@/graphql/generated';
import type { GetContractReconciliationAvailableForPeriodQuery, OrderReconciliationView } from '@/graphql/generated';
import { isOperationDisabled, isOperationVisible } from '@/core/utils/permissions';
import PlaceholderImage from '@/assets/img/pages/reconciliation/placeholder_image.svg';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import Pagination from '@/core/components/common/Pagination.vue';
import EDataTable from '@/core/components/common/data-items/EDataTable.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EAlert, EBtn, ECheckbox, ETooltip, HelpTooltip } from '@/core/components/ui';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { getDatePeriod } from '@/core/utils/date';
import { formatPrice } from '@/core/utils/number';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import ListContextMenu from '@/parts/reconciliation/components/list-context-menu.vue';
import ReconciliationDetailsDialog from '@/parts/reconciliation/components/order-reconciliation-details.vue';
import type { DataTableHeaders } from '@/core/types/components/table';

const isDetailsDialogOpen = ref(false);
const loadingReconciliationId = ref<number | null>(null);
const selectedReconciliation = ref<OrderReconciliationView | null>(null);
const reconciliationStatus = ref<GetContractReconciliationAvailableForPeriodQuery['contractReconciliationAvailableForPeriod'] | null>(null);
const isCompletingPeriodReconciliation = ref(false);
const isPeriodAvailableDialogOpen = ref(false);
const isMismatchFromMenu = ref(false);

const onReconcileClick = async () => {
  if (selected.value.length === 0) return;

  const orderIds = selected.value.map((item) => item.commonInfo.orderId);

  const variables = { input: { orderIds } };
  const { request } = useReconcileOrdersReconciliationMutation(variables);
  await request();

  await getReconciliationsList();
  await getReconciliationStatuses();
  checkPeriodStatusAndShowDialog();

  selected.value = [];
};

const handleOrderReconciled = async () => {
  await getReconciliationsList();
  await getReconciliationStatuses();
  checkPeriodStatusAndShowDialog();
};

const isRefreshingFromDialog = ref(false);

const openReconciliationDialog = async (item: Reconciliation, fromMismatchMenu: boolean = false) => {
  loadingReconciliationId.value = item.commonInfo.orderId;

  const { request } = useGetOrderReconciliationQuery({
    orderId: item.commonInfo.orderId,
  });

  const response = await request();

  if (response.data?.orderReconciliation) {
    selectedReconciliation.value = response.data.orderReconciliation;
    isMismatchFromMenu.value = fromMismatchMenu; 
  }

  await nextTick();

  isDetailsDialogOpen.value = true;
  loadingReconciliationId.value = null;
};


const handleContextMenuViewDetails = async (item: Reconciliation) => {
  await openReconciliationDialog(item, false); 
};

const handleContextMenuMismatch = async (item: Reconciliation) => {
  await openReconciliationDialog(item, true);
};

const handleRowClick = async (event: Event, { item }: { item: Reconciliation }) => {
  loadingReconciliationId.value = item.commonInfo.orderId;
  await openReconciliationDialog(item, false);
  loadingReconciliationId.value = null;
};

const handleDialogAfterLeave = () => {
  isRefreshingFromDialog.value = false;
  isMismatchFromMenu.value = false;
};

const refreshData = async () => {
  await getReconciliationsList();
  await getReconciliationStatuses();
};

const handleStatusChanged = async () => {
  isRefreshingFromDialog.value = true;
  await refreshData();
  isRefreshingFromDialog.value = false;
};

const handleMismatch = async (payload: { 
  orderId: number; 
  reason: string; 
  description: string;
  status?: OrderReconciliationStatus;
}) => {
  isRefreshingFromDialog.value = true;
  await refreshData();
  isRefreshingFromDialog.value = false;
};

const handleReconcileFromDialog = async () => {
  isRefreshingFromDialog.value = true;
  await getReconciliationsList();
  await getReconciliationStatuses();
  checkPeriodStatusAndShowDialog();
  isRefreshingFromDialog.value = false;
  isDetailsDialogOpen.value = false;
};

const {
  getReconciliations,
  reconciliations,
  operations,
  getReconciliationFilters,
  reconciliationFilters,
  clearAll,
} = useReconciliationStore();
const { activeAccount } = useAccommodationObjectsStore();

const filters = ref<ReconciliationFilters>(getReconciliationDefaultFilters());

const pageWrapperRef = ref<InstanceType<typeof PageWrapper>>();

const selected = ref<Reconciliation[]>([]);

const isAllSelected = computed(() => selected.value.length === reconciliations.value?.ordersReconciliation.length);
const isPartialSelected = computed(() => !!selected.value.length);

const selectedAmountInfo = computed(() =>
  selected.value.reduce(
    (acc, item) => {
      acc.payout += item.commonInfo.amountsInfo.payout;
      acc.payable += item.commonInfo.amountsInfo.payable;
      return acc;
    },
    { payout: 0, payable: 0 },
  ),
);

const isReconciled = computed(() => 
  reconciliationStatus.value?.contractReconciliationState === ContractReconciliationStates.Reconciled
);

const reconciliationOperations = computed(() => operations.value);

const isReconciliationsLoading = ref(false);

const loadContractData = async (contractNumber: number) => {
  clearAll(); 
  
  filters.value = getReconciliationDefaultFilters();
  selected.value = [];
  selectedReconciliation.value = null;
  
  await getReconciliationFilters({
    input: { contractNumber },
  });
  
  if (!reconciliationFilters.value?.reportDates?.length) {
    filters.value.mounth = '';
    return;
  }
  
  filters.value.mounth = reconciliationFilters.value.reportDates[0].reportDate;
};

watch(
  () => activeAccount.value?.contractNumber,
  async (newContract, oldContract) => {
    if (newContract && newContract !== oldContract) {
      await loadContractData(newContract);
    }
  },
  { immediate: true }
);

watch(
  filters,
  async () => {
    if (!filters.value.mounth || !activeAccount.value) {
      return;
    }
    
    pageWrapperRef.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    
    await getReconciliationsList();
  },
  { deep: true }
);

watch(
  () => filters.value.mounth,
  async (newMonth, oldMonth) => {
    if (!filters.value.mounth || !activeAccount.value) {
      return;
    }
    
    if (newMonth !== oldMonth) {
      await getReconciliationStatuses();
    }
  }
);

const getReconciliationsList = async () => {
  if (!activeAccount.value || !filters.value.mounth) {
    return;
  }

  selected.value = [];

  isReconciliationsLoading.value = true;

  await getReconciliations({
    input: {
      contractNumber: activeAccount.value.contractNumber,
      page: filters.value.pagination.page,
      pageSize: filters.value.pagination.pageSize,
      filters: {
        reportingDate: filters.value.mounth,
        searchTerm: filters.value.searchString,
        status: (filters.value.status ? [filters.value.status] : undefined),
      },
      ...(filters.value.sort.key && SORT_COLUMN_MAP[filters.value.sort.key] ? {
        sort: {
          columnName: SORT_COLUMN_MAP[filters.value.sort.key],
          sortOrder: filters.value.sort.order,
        }
      } : {})
    },
  });

  isReconciliationsLoading.value = false;
};

const getReconciliationStatuses = async () => {
  if (!activeAccount.value || !filters.value.mounth) {
    selected.value = [];
    return;
  }

  const { request } = useGetContractReconciliationAvailableForPeriodQuery({
    input: {
      contractNumber: activeAccount.value.contractNumber,
      reportingDate: filters.value.mounth,
    }
  });

  const response = await request();

  if (response.data?.contractReconciliationAvailableForPeriod) {
    reconciliationStatus.value = response.data.contractReconciliationAvailableForPeriod;
  }
};

const onItemSelect = (item: Reconciliation) => {
  if (selected.value.includes(item)) {
    selected.value = selected.value.filter((selectedItem) => selectedItem !== item);
  } else {
    selected.value = selected.value.concat([item]);
  }
};

const onAllSelectedUpdate = (status: boolean) => {
  if (status || (!status && !isAllSelected.value)) {
    selected.value = reconciliations.value?.ordersReconciliation.map((item) => item) || [];
  } else {
    selected.value = [];
  }
};

const openCompletePeriodDialog = () => {
  isPeriodAvailableDialogOpen.value = true;
};

const completePeriodReconciliation = async () => {
  if (!activeAccount.value || !filters.value.mounth || isCompletingPeriodReconciliation.value) {
    return;
  }

  isCompletingPeriodReconciliation.value = true;
  
  const { request } = useReconcileContractForPeriodMutation({
    input: {
      contractNumber: activeAccount.value.contractNumber,
      reportingDate: filters.value.mounth,
    }
  });

  await request();

  await getReconciliationStatuses();
  await getReconciliationsList();
  selected.value = [];

  isCompletingPeriodReconciliation.value = false;
  isPeriodAvailableDialogOpen.value = false;
};

const checkPeriodStatusAndShowDialog = () => {
  if (reconciliationStatus.value?.contractReconciliationState === ContractReconciliationStates.AvailableToReconcile) {
    isPeriodAvailableDialogOpen.value = true;
  }
};

const headers: DataTableHeaders = [
  {
    key: 'commonInfo.orderNumber',
    title: '№ бронирования',
    width: 230,
  },
  {
    key: 'commonInfo.tourists',
    title: 'Гости',
    sortable: false,
  },
  {
    key: 'commonInfo.period',
    title: 'Заезд–Выезд',
    width: 230,
  },
  {
    key: 'commonInfo.orderAvoidanceStatus',
    title: 'Аннулировано',
  },
  {
    key: 'commonInfo.amountsInfo.payout',
    title: 'Выплаты',
    sortable: false,
    width: 230,
    align: 'end',
  },
  {
    key: 'commonInfo.amountsInfo.payable',
    title: 'К оплате',
    sortable: false,
    width: 230,
    align: 'end',
  },
  {
    key: 'commonInfo.orderReconciliationStatus',
    title: 'Статус',
  },
  {
    key: 'actions',
    sortable: false,
    width: 48,  
  },
];
</script>

<style scoped lang="scss">
.reconciliation-mobile-floating-bar {
  display: flex;
  align-items: center;

  min-height: 48px;

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    min-width: 150px;
  }
}

.table-wrapper {
  position: relative;
}

.table-loading {
  position: relative;

  @include disable-loader;

  :deep(.v-data-table__wrapper) {
    position: relative;
    z-index: 1;
  }
}
</style>
